/**
 * ServiceTagsAndCustomFields — combined "Tags & Custom Fields" section for
 * the Service detail page (issue #746, reworked #794).
 *
 * Merges the previously separate cards into one:
 *  - `ServiceCustomFields` (#184) — definition-driven, read-only display. One
 *    row per active `TagCategory` whose `applicableEntityTypes` includes
 *    `Service`. Typed categories show the formatted `Tag.value`; label-only
 *    categories show assigned tags as chips.
 *  - `ServiceTagAssignment` (#656) — editable, full-catalog tag assignment
 *    via `TagPicker`, gated behind `service:update`, honoring each
 *    category's `allowMultiple` single-select constraint.
 *
 * Issue #794 rework — a single category-aware picker replaces the earlier
 * per-category picker rows (which rendered one row per service-applicable
 * category, always shown when editable even if unset — a long list of empty
 * rows):
 *  - **Typed-value rows** — one per service-applicable category whose
 *    `valueType` is *not* label-only, shown only when it has an assigned
 *    tag (both read-only and editable) — the formatted `Tag.value` (e.g.
 *    `1250000`) next to the category name. Read-only info only; the tag
 *    itself is added/removed through the picker below.
 *  - **Tags row** — a single `TagPicker` (editable) covering *every*
 *    assigned tag — governed (label-only or typed) and ungoverned alike —
 *    with `categories` passed through and `enforceSingleSelect` so a
 *    single-select (`allowMultiple: false`) category whose slot is filled
 *    excludes its other tags from the add options until the assigned chip
 *    is removed. The picker's own chips already carry category context
 *    (issue #714), so no per-category grouping is needed here. Without
 *    `service:update`, this row falls back to a plain read-only chip list
 *    (or "No tags assigned.").
 *
 * The backend always enforces `service:update` on the write endpoints — the
 * `usePermission` check here is a UX aid, not a security boundary (mirrors
 * `PermissionGate`'s own documented posture).
 */

import { useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useAllTags, useTagCategories, useTagsByEntity } from '../../graphql/queries';
import type { Tag, TagCategory, TagValue, TagValueType } from '../../graphql/types';
import { TagPicker } from '../tags/TagPicker';
import { TagBadge } from '../tags/TagBadge';
import { resolveTagCategoryName } from '../../lib/tagCategoryName';
import { usePermission } from '../../hooks/usePermission';
import { addTagToEntity, removeTagFromEntity, ApiError } from '../../lib/apiClient';
import './ServiceTagsAndCustomFields.css';
import '@tylertech/forge/card';
import '@tylertech/forge/divider';
import '@tylertech/forge/banner';

const SERVICE_ENTITY_TYPE = 'Service';

/**
 * A category is "label-only" when it carries no typed value. The REST/write
 * path uses the hyphenated internal value `label-only`, but graphql-js
 * serializes the enum to its schema *name* `label_only` (enum names cannot
 * contain hyphens), so a category read via GraphQL arrives as `label_only`.
 * Accept both (issue #644).
 */
const LABEL_ONLY_VALUE_TYPES = new Set(['label-only', 'label_only']);

export interface ServiceTagsAndCustomFieldsProps {
  serviceId: string;
}

/** True when a category applies to services (empty/absent applicableEntityTypes ⇒ all types). */
function appliesToService(category: TagCategory): boolean {
  const types = category.applicableEntityTypes;
  return !types || types.length === 0 || types.includes(SERVICE_ENTITY_TYPE);
}

/** Format a typed tag value for display. */
function formatValue(value: TagValue | null | undefined, valueType: TagValueType): string {
  if (value === null || value === undefined || value === '') return '—';
  if (valueType === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function ServiceTagsAndCustomFields({
  serviceId,
}: ServiceTagsAndCustomFieldsProps): JSX.Element {
  const apolloClient = useApolloClient();
  const canEdit = usePermission('service:update');

  const { data: categoriesData } = useTagCategories();
  const { data: entityTagsData, loading: entityTagsLoading } = useTagsByEntity(
    serviceId,
    SERVICE_ENTITY_TYPE
  );
  // Full catalog, not a single capped page (issue #1352) — this section
  // needs every tag to resolve `addedTag`/category lookups for any tag the
  // picker below can offer, matching the pattern used by `useAllTags`'s
  // other callers (e.g. TechnologyCapability detail).
  const { tags: allTags } = useAllTags();

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categories = categoriesData?.tagCategories ?? [];
  const serviceCategories = [...categories.filter(appliesToService)].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.name.localeCompare(b.name)
  );
  const typedServiceCategories = serviceCategories.filter(
    (c) => !LABEL_ONLY_VALUE_TYPES.has(c.valueType)
  );

  const assignedTags: Tag[] = entityTagsData?.tagsByEntity ?? [];
  const assignedTagIds = assignedTags.map((t) => t.entityId);

  async function refetchAssignedTags(): Promise<void> {
    await apolloClient.refetchQueries({ include: ['TagsByEntity'] });
  }

  /**
   * Applies a picker's add/remove delta for one scope — a single category
   * row, or the general "Other Tags" area. `prevIds`/`newIds` are the
   * scope's previously- and newly-selected tag ids; the diff between them is
   * exactly what changed regardless of scope size, so the single-select swap
   * logic from `ServiceTagAssignment` (#656) applies uniformly to every row.
   */
  async function applyChange(newIds: string[], prevIds: string[]): Promise<void> {
    const added = newIds.filter((id) => !prevIds.includes(id));
    const removed = prevIds.filter((id) => !newIds.includes(id));

    setError(null);
    setIsPending(true);
    try {
      if (added.length > 0) {
        const addedTagId = added[0];
        const addedTag = allTags.find((t) => t.entityId === addedTagId);
        const category = categories.find((c) => c.entityId === addedTag?.categoryId);

        // Single-select category: drop any existing tag from the same
        // category before adding the new one (issue #656 AC — tag-group
        // constraints respected).
        if (addedTag && category && !category.allowMultiple) {
          const conflicting = assignedTags.filter(
            (t) => t.categoryId === addedTag.categoryId && t.entityId !== addedTagId
          );
          for (const tag of conflicting) {
            await removeTagFromEntity(SERVICE_ENTITY_TYPE, serviceId, tag.entityId);
          }
        }

        await addTagToEntity(SERVICE_ENTITY_TYPE, serviceId, addedTagId);
      } else if (removed.length > 0) {
        await removeTagFromEntity(SERVICE_ENTITY_TYPE, serviceId, removed[0]);
      }

      await refetchAssignedTags();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update tags');
    } finally {
      setIsPending(false);
    }
  }

  const CardElement = 'forge-card' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  // Read-only info rows for typed categories with an assigned tag — the
  // unified picker below only ever shows the tag's *name*, so typed values
  // (e.g. `1250000`) still need this formatted read-out (issue #794).
  const typedValueRows = typedServiceCategories
    .map((category) => {
      const categoryTags = assignedTags.filter((t) => t.categoryId === category.entityId);
      if (categoryTags.length === 0) return null;

      const valueContent = categoryTags
        .map((t) => formatValue(t.value, category.valueType))
        .join(', ');

      return (
        <div className="detail-row" key={category.entityId} data-testid="custom-field-value-row">
          <span className="detail-label">{category.name}</span>
          <span className="detail-value">{valueContent || '—'}</span>
        </div>
      );
    })
    .filter((row): row is JSX.Element => row !== null);

  return (
    <CardElement className="service-card" data-testid="service-custom-fields">
      <div className="card-header">
        <h2 className="forge-typography--headline6">Tags &amp; Custom Fields</h2>
      </div>
      <DividerElement />
      <div className="card-content">
        {error && (
          <BannerElement
            theme="error"
            dismissed={false}
            role="alert"
            className="service-tags__error"
            data-testid="service-tags-error"
          >
            <span>{error}</span>
          </BannerElement>
        )}

        {typedValueRows}

        <div className="detail-row" data-testid="service-tags-row">
          <span className="detail-label">Tags</span>
          <span className="detail-value">
            {canEdit ? (
              <TagPicker
                id="service-tag-picker"
                label="Tags"
                hideLabel
                selectedTagIds={assignedTagIds}
                onChange={(newIds) => applyChange(newIds, assignedTagIds)}
                disabled={isPending || entityTagsLoading}
                categories={categories}
                enforceSingleSelect
              />
            ) : assignedTags.length === 0 ? (
              <p className="forge-typography--body2">No tags assigned.</p>
            ) : (
              <span className="custom-field-tags">
                {assignedTags.map((t) => (
                  <TagBadge
                    key={t.entityId}
                    tag={t}
                    categoryName={resolveTagCategoryName(t.categoryId, categories)}
                  />
                ))}
              </span>
            )}
          </span>
        </div>
      </div>
    </CardElement>
  );
}

export default ServiceTagsAndCustomFields;
